import React, { useEffect } from 'react';
import Clarity from '@microsoft/clarity';
import './App.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import PrivateRoute from "./components/routeAuthorization/privateRoute";
import PublicRoute from "./components/routeAuthorization/publicRoute";
import RoleRoute from "./components/routeAuthorization/roleRoute";

import { RoleProvider } from "./context/RoleContext";
import { InstitutionProvider } from "./context/InstitutionContext";
import { UserProvider } from "./context/UserContext";
import { MantenedoraProvider } from "./context/MantenedoraContext";
import { ImportFlowProvider } from "./context/ImportFlowContext";
import { useInstitutionTheme } from "./hooks/useInstitutionTheme";

import AppLayout from "./pages/app/appLayout";
import AuthLayout from "./pages/auth/authLayout";

import Users from "./pages/app/users/users";
import Login from "./pages/auth/login/login";
import Signup from "./pages/auth/signup/signup";
import ForgotPassword from "./pages/auth/forgot-password/forgotPassword";
import CreateMantenedora from "./pages/auth/create-mantenedora/createMantenedora";
import InviteComplete from "./pages/auth/inviteComplete/inviteComplete";
import Finance from "./pages/app/finance/finance";
import Regulatorio from "./pages/app/regulatorio/regulatorio";
import Education from "./pages/app/education/education";
import Insights from "./pages/app/insights/insights";
import Prediction from "./pages/app/prediction/prediction";
import Inadimplentes from "./pages/app/inadimplentes/inadimplentes";
import Defaulting from "./pages/app/defaulting/defaulting";
import Settings from "./pages/app/settings/settings";
import Notifications from "./pages/app/notifications/notifications";
import Dashboard from "./pages/app/dashboard/dashboard";
import Overview from "./pages/app/overview/overview";
import ImportData from "./pages/app/importData/importData";
import ImportDataReview from "./pages/app/importData/importDataReview/importDataReview";
import ImportDataReviewSmart from "./pages/app/importData/importDataReviewSmart/importDataReviewSmart";
import UserEdit from "./pages/app/users/UserEdit/userEdit";
import UsersRegister from "./pages/app/users/UserRegister/userRegister";
import ConsolidatedData from "./pages/app/consolidatedData/consolidatedData";
import EducationConsolidatedData from "./pages/app/educationConsolidatedData/educationConsolidatedData";
import ClassConsolidatedData from "./pages/app/classConsolidatedData/classConsolidatedData";
import AdhesionConsolidatedData from "./pages/app/adhesionConsolidatedData/adhesionConsolidatedData";
import TeacherConsolidatedData from "./pages/app/teacherConsolidatedData/teacherConsolidatedData";
import RawImportedData from "./pages/app/rawImportedData/rawImportedData";
import CustomDashboard from "./pages/app/customDashboard/customDashboard";
import Profile from "./pages/app/profile/profile";
import MantenedoraUsers from "./pages/app/mantenedoraUsers/mantenedoraUsers";
import MantenedoraUserEdit from "./pages/app/mantenedoraUsers/MantenedoraUserEdit/mantenedoraUserEdit";
import MantenedoraInstitutions from "./pages/app/mantenedoraInstitutions/mantenedoraInstitutions";
import Mantenedora from "./pages/app/mantenedora/mantenedora";
import NewInstitution from "./pages/app/newInstitution/newInstitution";
import SelectInstitution from "./pages/auth/select-institution/selectInstitution";
import DropoutRisk from "./pages/app/dropoutRisk/dropoutRisk";
import ContractPlan from "./pages/app/contractPlan/contractPlan";
import ContractPending from "./pages/app/contractPending/contractPending";

// Componente que aplica o tema da instituição
const ThemeApplier = ({ children }) => {
  useInstitutionTheme();
  return children;
};

function App() {

  useEffect(() => {
    if (
      process.env.REACT_APP_ENV !== 'local' &&
      process.env.REACT_APP_CLARITY_ID
    ) {
      console.log('Clarity.js initialized');
      Clarity.init(process.env.REACT_APP_CLARITY_ID);
    }
  }, []);


  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
        progress={undefined}
        theme="colored"
      />

      <RoleProvider>
        <InstitutionProvider>
          <MantenedoraProvider>
            <ThemeApplier>
              <UserProvider>
                <Router>
            <Routes>
              <Route path="/" element={<Navigate to="/auth/login" replace />} />

              <Route
                path="/auth"
                element={
                  <PublicRoute>
                    <AuthLayout />
                  </PublicRoute>
                }
              >
                <Route index element={<Navigate to="login" replace />} />
                <Route path="login" element={<Login />} />
                <Route path="signup" element={<Signup />} />
                <Route path="forgot-password" element={<ForgotPassword />} />
                <Route path="create-mantenedora" element={<CreateMantenedora />} />
              </Route>

              <Route
                path="/invite"
                element={
                  <PublicRoute>
                    <AuthLayout />
                  </PublicRoute>
                }
              >
                <Route path="complete" element={<InviteComplete />} />
              </Route>

              <Route
                path="/select-institution"
                element={
                  <PrivateRoute>
                    <AuthLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<SelectInstitution />} />
              </Route>

              <Route
                path="/app"
                element={
                  <PrivateRoute>
                    <ImportFlowProvider>
                      <AppLayout />
                    </ImportFlowProvider>
                  </PrivateRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Overview />} />
                {/* Dashboard analítico antigo: fora do menu, mantido para links salvos. */}
                <Route path="dashboard-analitico" element={<Dashboard />} />
                <Route path="meu-perfil" element={<Profile />} />
                <Route path="usuarios" element={<Users />} />
                <Route path="usuarios/register" element={<UsersRegister />} />
                <Route path="usuarios/edit/:id" element={<UserEdit />} />
                <Route
                  path="gestao-usuarios"
                  element={
                    <RoleRoute
                      allowedRoles={["MantenedoraAdmin", "MantenedoraManager"]}
                      redirectTo="/app/usuarios"
                    >
                      <MantenedoraUsers />
                    </RoleRoute>
                  }
                />
                <Route
                  path="gestao-usuarios/edit/:userId"
                  element={
                    <RoleRoute
                      allowedRoles={["MantenedoraAdmin", "MantenedoraManager"]}
                      redirectTo="/app/usuarios"
                    >
                      <MantenedoraUserEdit />
                    </RoleRoute>
                  }
                />
                <Route
                  path="instituicoes"
                  element={
                    <RoleRoute
                      allowedRoles={["MantenedoraAdmin", "MantenedoraManager"]}
                      redirectTo="/app/dashboard"
                    >
                      <MantenedoraInstitutions />
                    </RoleRoute>
                  }
                />
                <Route
                  path="nova-instituicao"
                  element={
                    <RoleRoute
                      allowedRoles={["MantenedoraAdmin", "MantenedoraManager"]}
                      redirectTo="/app/dashboard"
                    >
                      <NewInstitution />
                    </RoleRoute>
                  }
                />
                <Route
                  path="contratar-plano"
                  element={
                    <RoleRoute
                      allowedRoles={["Admin", "MantenedoraAdmin"]}
                      redirectTo="/app/dashboard"
                    >
                      <ContractPlan />
                    </RoleRoute>
                  }
                />
                <Route
                  path="contratacao-pendente"
                  element={
                    <RoleRoute
                      allowedRoles={["Admin", "MantenedoraAdmin"]}
                      redirectTo="/app/dashboard"
                    >
                      <ContractPending />
                    </RoleRoute>
                  }
                />
                <Route
                  path="mantenedora"
                  element={
                    <RoleRoute
                      allowedRoles={["MantenedoraAdmin", "MantenedoraManager"]}
                      redirectTo="/app/dashboard"
                    >
                      <Mantenedora />
                    </RoleRoute>
                  }
                />
                <Route path="configuracoes" element={<Settings />} />
                <Route path="notificacoes" element={<Notifications />} />
                <Route path="importar-dados" element={<ImportData />} />
                <Route path="importar-dados/revisao" element={<ImportDataReview />} />
                <Route path="importar-dados/revisao-inteligente" element={<ImportDataReviewSmart />} />
                <Route path="dados-consolidados" element={<ConsolidatedData />} />
                <Route path="dados-consolidados-academicos" element={<EducationConsolidatedData />} />
                <Route path="dados-consolidados-turmas" element={<ClassConsolidatedData />} />
                <Route path="dados-consolidados-adesao" element={<AdhesionConsolidatedData />} />
                <Route path="dados-consolidados-docentes" element={<TeacherConsolidatedData />} />
                <Route path="dados-brutos-importados" element={<RawImportedData />} />
                <Route path="financeiro" element={<Finance />} />
                <Route path="regulatorio" element={<Regulatorio />} />
                <Route path="academico" element={<Education />} />
                <Route path="insights" element={<Insights />} />
                <Route path="predicao" element={<Prediction />} />
                <Route path="inadimplentes" element={<Inadimplentes />} />
                <Route path="alunos-inadimplentes" element={<Defaulting />} />
                <Route path="possiveis-evasores" element={<DropoutRisk />} />
                <Route path="dashboard-customizado" element={<CustomDashboard />} />
                <Route path="dashboard-customizado/criar" element={<CustomDashboard />} />
                <Route path="dashboard-customizado/:dashboardId" element={<CustomDashboard />} />
                <Route path="dashboard-customizado/:dashboardId/editar" element={<CustomDashboard />} />
              </Route>
            </Routes>
                </Router>
              </UserProvider>
            </ThemeApplier>
          </MantenedoraProvider>
        </InstitutionProvider>
      </RoleProvider>
    </>
  );
}

export default App;
